<template>
    <div class="receipt-einvoice invoice">

        <!-- Invoice Header -->
        <template v-if="!order.isIncoming()">
            <div
                v-if="venue.invoice_header"
                class="ck-content ck-custom"
                v-html="venue.invoice_header"
            />

            <div
                v-else
                class="receipt-header"
            >
                <p>{{ venue.company_name }}</p>
                <p>{{ venue.name }}</p>
                <p>{{ venue.address }}</p>
            </div>
        </template>

        <div class="nonfiscal-heading">
            <p v-if="!preview && order.number">{{ $t('receipt.serial_number') + ': ' + order.number }}</p>
            <p v-if="!preview && order.internal_number">
                {{ `${!order.isProforma() ? $t('receipt.internal_number') : $t('receipt.proforma_number')}: ${order.internal_number}` }}
            </p>
            <p v-if="preview && options.internalNumber">
                {{ `${!options.isProforma ? $t('receipt.internal_number') : $t('receipt.proforma_number')}: ${options.internalNumber}` }}
            </p>
            <p v-if="order.due_date">{{ $t('receipt.due_date') }}: {{ order.due_date | date }}</p>
            <p v-if="order.start_date && order.end_date">
                {{ $t('receipt.period') }}: {{ order.start_date | date }} - {{ order.end_date | date }}
            </p>
        </div>

        <div class="receipt-subheader">

            <!-- Receipt Details -->
            <div class="receipt-details">
                <p v-if="venue.company_name">{{ $t('receipt.company') }}: {{ venue.company_name }}</p>
                <p v-if="venue.tax_id_number">{{ $t('receipt.tax_id_number') }}: {{ venue.tax_id_number }}</p>
                <p v-if="venue.registration_number">{{ $t('receipt.registration_number') }}: {{ venue.registration_number }}</p>
                <p v-if="venue.name">{{ $t('receipt.venue') }}: {{ venue.name }}</p>
                <p v-if="venue.address">{{ $t('receipt.address') }}: {{ venue.address }}</p>
                <p v-if="venue.city">{{ $t('receipt.municipality') }}: {{ venue.city }}</p>
            </div>

            <!-- Invoice Client Details -->
            <div v-if="client" class="receipt-buyer">
                <p>
                    <strong v-if="!order.isIncoming()">{{ $t('receipt.buyer') }}</strong>
                    <strong v-else>{{ $tc('general.supplier', 1) }}</strong>
                </p>
                <p v-if="client.name">{{ client.name }}</p>
                <p v-if="client.address">
                    {{ `${client.address}` }}
                    <template v-if="client.city">, {{ `${client.city}` }}</template>
                    <template v-if="client.country">, {{ `${client.country}` }}</template>
                </p>
                <p v-if="client.getTaxIdNumber()">
                    {{ $t('receipt.buyer_tax_id_number') + ': ' + client.getTaxIdNumber() }}
                </p>
                <p v-if="client.cost_center">
                    {{ $t('receipt.buyer_cost_center') + ': ' + client.cost_center }}
                </p>
                <p v-if="client.registration_number">
                    {{ $t('receipt.registration_number') + ': ' + client.registration_number }}
                </p>
            </div>

        </div>

        <!-- Receipt Items -->
        <table v-if="!venue.wholesale" class="receipt-items">
            <tbody>

                <!-- Heading Row -->
                <tr class="heading-row">
                    <td align="left">{{ $t('receipt.items.number') }}</td>
                    <td align="left">{{ $t('receipt.items.code') }}</td>
                    <td align="left">{{ $t('receipt.items.name') }}</td>
                    <td align="right">{{ $t('receipt.items.unit_price') }}</td>
                    <td align="right">{{ $t('receipt.items.quantity') }}</td>
                    <td align="right">{{ $t('receipt.items.to_pay') }}</td>
                </tr>

                <!-- Order Items -->
                <tr
                    v-for="(item, index) in order.items"
                    :key="`invoice-${index}`"
                    :class="{ 'border-bottom': index !== order.items.length - 1 }"
                >
                    <td align="left">{{ index + 1 }}.</td>
                    <td align="left">
                        <span v-if="item.getProduct()">
                            {{ item.getProduct().code }}
                        </span>
                    </td>
                    <td align="left" class="name">
                        <span>{{ item.name }}</span>
                        <span v-if="item.getTaxRateLabel()"> ({{ item.getTaxRateLabel() }})</span>
                        <span v-if="item.getUnitOfMeasure()">/{{ item.getUnitOfMeasure() }}</span>
                        <div v-if="isAllegra && item.comments.length" v-html="nl2br(item.comments[0])"></div>
                        <div v-if="item.gtin" style="font-size: 11px;">GTIN: {{ item.gtin }}</div>
                    </td>
                    <td align="right">{{ item.getUnitPriceAfterDiscount().abs().toFormat('0.00') }}</td>
                    <td align="right">{{ Math.abs(item.getCount()) }}</td>
                    <td align="right">{{ item.getToPay().toFormat('0.00') }}</td>
                </tr>

                <tr class="divider">
                    <td :colspan="12" />
                </tr>
            </tbody>
        </table>

        <!-- Receipt Items -->
        <table v-else class="receipt-items">
            <tbody>

                <!-- Heading Row -->
                <tr class="heading-row">
                    <td align="left">{{ $t('receipt.items.number') }}</td>
                    <td align="left">{{ $t('receipt.items.code') }}</td>
                    <td align="left">{{ $t('receipt.items.name') }}</td>
                    <td align="right">{{ $t('receipt.items.quantity') }}</td>
                    <td align="right" width="70px">{{ $t('receipt.items.wholesale.unit_price_before_tax') }}</td>
                    <td align="right">{{ $t('receipt.items.wholesale.tax_rate') }}</td>
                    <td align="right" width="70px">{{ $t('receipt.items.wholesale.unit_price') }}</td>
                    <td align="right">{{ $t('receipt.items.wholesale.discount') }}</td>
                    <td align="right" width="90px">{{ $t('receipt.items.wholesale.unit_price_after_discount') }}</td>
                    <td align="right" width="90px">{{ $t('receipt.items.wholesale.price_before_tax') }}</td>
                    <td align="right" width="70px">{{ $t('receipt.items.wholesale.tax') }}</td>
                    <td align="right" width="80px">{{ $t('receipt.items.wholesale.to_pay') }}</td>
                </tr>

                <!-- Order Items -->
                <template v-for="(item, index) in order.items">
                    <tr
                        :key="`invoice-${index}`"
                        :class="{ 'border-bottom': (!isAllegra || !item.comments.length) && index != order.items.length - 1 }"
                    >
                        <td align="left">{{ index + 1 }}.</td>
                        <td align="left">
                            {{ item.getProduct() && item.getProduct().code ? item.getProduct().code : '' }}
                        </td>
                        <td align="left" class="name">
                            <span>{{ item.name }}</span>
                            <span v-if="item.getTaxRateLabel()"> ({{ item.getTaxRateLabel() }})</span>
                            <span v-if="item.getProduct()">/{{ item.getProduct().getUnitOfMeasureSold() }}</span>
                            <div v-if="item.gtin" style="font-size: 11px;">GTIN: {{ item.gtin }}</div>
                        </td>
                        <td align="right">{{ Math.abs(item.getCount()) }}</td>
                        <td align="right">{{ item.getUnitPriceBeforeTax().toFormat('0.0000') }}</td>
                        <td align="right">{{ item.getTaxRate() }}%</td>
                        <td align="right">{{ item.getUnitPriceAfterTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getDiscountPercent() }}%</td>
                        <td align="right">{{ item.getUnitPriceAfterDiscount().abs().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getPriceBeforeTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getTax().toFormat('0.00') }}</td>
                        <td align="right">{{ item.getToPay().toFormat('0.00') }}</td>
                    </tr>

                    <tr
                        v-if="isAllegra && item.comments.length" :key="`invoice-item-comment-${index}`"
                        :class="{ 'border-bottom': index != order.items.length - 1 }"
                    >
                        <td></td>
                        <td></td>
                        <td colspan="10">
                            <span style="font-size: 11px;" v-html="nl2br(item.comments[0])"></span>
                        </td>
                    </tr>
                </template>

                <tr class="divider">
                    <td :colspan="12" />
                </tr>
            </tbody>
        </table>

        <div class="receipt-footer">
            <div class="footer-item additional-info">
                <span class="code">
                    <template v-if="options.isProforma || order.isProforma()">
                        {{ $t('general.receipt_type') + ': ' + $tc('general.proforma', 1) }}
                    </template>
                    <template v-else-if="order.isEinvoice()">
                        {{ $t('general.receipt_type') + ': ' + $tc('general.einvoice', 1) }}
                    </template>
                    <template v-if="order.isAdvance()">
                        {{ ' / ' + $t('general.advance') }}
                    </template>
                    <template v-if="order.isEinvoice() && order.register_status">
                        {{ '(' + $t(`order.register_status.einvoice.${order.register_status}`) + ')' }}
                    </template>
                </span>

                <span class="code">
                    {{ $t('receipt.time') }}: {{ preview ? moment() : order.getLastActivityAt() | dateTime }}
                </span>

                <span v-if="order.getFlag('otaSync').length" class="code">
                    {{ $t('integrations.ota_sync.room_number') }}: {{ order.getFlag('otaSync')[0] }}
                </span>

                <span v-if="registerRequest.delivery_at" class="code">
                    {{ $t('einvoice.delivery_at') }}: {{ registerRequest.delivery_at | date }}
                </span>

                <div v-if="attachments.length" class="code">
                    {{ $tc('einvoice.attachments', attachments.length == 1 ? 1 : 2) }}:
                    <span v-for="(attachment, index) in attachments">
                        <a :href="attachment.url" target="_blank">{{ attachment.name }}</a>
                        <template v-if="index != attachments.length - 1">,&nbsp</template>
                    </span>
                </div>

                <div v-if="show.einvoiceInfo" class="mt-2">
                    <span v-if="registerRequest.contract_number" class="code">
                        {{ $t('einvoice.contract_number') }}: {{ registerRequest.contract_number }}
                    </span>

                    <span v-if="registerRequest.invoice_number" class="code">
                        {{ $t('einvoice.invoice_number') }}: {{ registerRequest.invoice_number }}
                    </span>

                    <span v-if="registerRequest.agreement_number" class="code">
                        {{ $t('einvoice.agreement_number') }}: {{ registerRequest.agreement_number }}
                    </span>

                    <span v-if="registerRequest.receipt_number" class="code">
                        {{ $t('einvoice.receipt_number') }}: {{ registerRequest.receipt_number }}
                    </span>

                    <span v-if="registerRequest.dispatch_number" class="code">
                        {{ $t('einvoice.dispatch_number') }}: {{ registerRequest.dispatch_number }}
                    </span>

                    <span v-if="registerRequest.project_number" class="code">
                        {{ $t('einvoice.project_number') }}: {{ registerRequest.project_number }}
                    </span>
                </div>

                <div v-if="show.note" class="mt-2">
                    <p v-if="order.note" class="note">
                        {{ order.note }}
                    </p>

                    <p v-if="registerRequest.comment" class="note">
                        {{ registerRequest.comment }}
                    </p>

                    <p v-if="registerRequest.storno_comment" class="note">
                        {{ registerRequest.storno_comment }}
                    </p>

                    <p v-if="!venue.taxes_enabled" class="note">
                        {{ $t('general.note') }}: {{ $t('order.exempt_from_tax_comment') }}
                    </p>
                </div>

                <div class="signatures">
                    <div class="signature">
                        <div>___________________</div>
                        <div>{{ $tc('invoice.delivered_by') }}</div>
                    </div>

                    <div class="signature">
                        <div>___________________</div>
                        <div>{{ $tc('invoice.received_by') }}</div>
                    </div>
                </div>
            </div>

            <div class="footer-item summary">
                <div v-if="order.getToPay().toUnit() > 0" class="summary-item to-pay">
                    <p>{{ $t('receipt.payments.to_pay_sale') }}:</p>
                    <p class="amount">
                        {{ order.getToPay().abs().toFormat('0.00') }}
                    </p>
                </div>

                <div v-else class="summary-item to-pay">
                    <p>{{ $t('receipt.payments.to_pay_refund') }}:</p>
                    <p class="amount">
                        {{ order.getToPay().abs().toFormat('0.00') }}
                    </p>
                </div>

                <div v-for="(paymentType, index) in paymentTypes" :key="index" class="summary-item">
                    <p>{{ paymentType.display }}:</p>
                    <p class="amount">{{ paymentType.amount.abs().toFormat('0.00') }}</p>
                </div>

                <table class="tax-items">
                    <tbody>
                        <tr class="heading-row">
                            <td align="left">{{ $t('receipt.taxes.label') }}</td>
                            <td align="center">{{ $t('receipt.taxes.name') }}</td>
                            <td align="right">{{ $t('receipt.taxes.rate') }}</td>
                            <td align="right">{{ $t('receipt.taxes.amount') }}</td>
                        </tr>

                        <tr v-for="(taxItem, index) in order.taxes" :key="index">
                            <td align="left">{{ taxItem.label }}</td>
                            <td align="center">{{ taxItem.name }}</td>
                            <td align="right">{{ taxItem.rate }}%</td>
                            <td align="right">{{ taxItem.getValue().abs().toFormat('0.00') }}</td>
                        </tr>

                        <tr v-if="venue.wholesale" class="bordered">
                            <td colspan="3">
                                {{ $t('receipt.taxes.total_price_before_tax') }}:
                            </td>
                            <td align="right">
                                {{ summary.totalPriceBeforeTax.toFormat('0.00') }}
                            </td>
                        </tr>

                        <tr class="bordered">
                            <td colspan="3">
                                {{ $t('receipt.taxes.total') }}:
                            </td>
                            <td align="right">
                                {{ summary.totalTax.toFormat('0.00') }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

    </div>
</template>

<script>
import Client from '@/models/Client';

export default {
    props: {
        order: {
            type: Object,
            default: () => null
        },
        options: {
            type: Object,
            default: () => {}
        },
        preview: {
            type: Boolean,
            default: false
        }
    },

    computed: {
        venue() {
            return this.options.venue || this.tenant?.venue
        },

        client() {
            return this.order.client || Client.find(this.order.client_id)
        },

        paymentTypes() {
            if(this.options.isProforma) {
                if(!this.options.paymentType) return [];

                return [{
                    display: this.$t(`receipt.payments.${this.options.paymentType.toLowerCase()}`),
                    amount: this.order.getLeftToPay()
                }];
            }

            if(this.preview) {
                let paymentTypes = [];
                if(this.order.getPaidAdvance().toUnit() > 0) {
                    paymentTypes.push({
                        display: this.$t('receipt.payments.advance'),
                        amount: this.order.getPaidAdvanceEffective()
                    });
                }

                if(this.order.getLeftToPay().toUnit() !== 0) {
                    paymentTypes.push({
                        display: this.$t('receipt.payments.wiretransfer'),
                        amount: this.order.getLeftToPay()
                    });
                }

                return paymentTypes.filter(type => type.amount.toUnit() != 0);
            }

            const totalPaid = this.order.getPaid().abs();
            const totalAmount = this.order.getToPay().abs();

            const remaining = this.order.isAdvance()
                ? Money.parse(0)
                : Money.parse(totalAmount.toRoundedUnit(2)).subtract(Money.parse(totalPaid.toRoundedUnit(2)))

            let allPaymentTypes = [];
            let paymentTypes = this.order.getPayments();
            const advancePayment = paymentTypes.find(p => p.value === 'advance')

            if(advancePayment) {
                allPaymentTypes.push(advancePayment)
                paymentTypes = paymentTypes.filter(p => p.value !== 'advance')
            }

            allPaymentTypes = [...allPaymentTypes, ...paymentTypes];

            if(remaining.toUnit() !== 0) {
                allPaymentTypes.push({
                    display: remaining.toUnit() < 0 ? this.$t('receipt.payments.change') : this.$t('receipt.payments.left_to_pay'),
                    amount: remaining.abs()
                })
            }

            return allPaymentTypes
        },

        summary() {
            const totalAmount = this.order.getToPay().abs();
            const totalTax = this.order.getTax().abs();
            const totalPriceBeforeTax = this.order.getPriceBeforeTax().abs();

            return {
                totalAmount: totalAmount,
                totalTax: totalTax,
                totalPriceBeforeTax: totalPriceBeforeTax,
            }
        },

        isEinvoice() {
            return !!this.options.isEinvoice || !!this.order.isEinvoice();
        },

        attachments() {
            if(!this.registerRequest?.attachments?.length) return [];
            return this.registerRequest.attachments;
        },

        registerRequest() {
            return this.order.register_request || this.options.registerRequest || {};
        },

        show() {
            return {
                note: this.order.note
                    || this.registerRequest.comment
                    || this.registerRequest.storno_comment
                    || !this.venue.taxes_enabled,
                einvoiceInfo: this.registerRequest.contract_number
                    || this.registerRequest.invoice_number
                    || this.registerRequest.agreement_number
                    || this.registerRequest.receipt_number
                    || this.registerRequest.dispatch_number
                    || this.registerRequest.project_number
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.receipt-einvoice {
    font-family: Arial, Helvetica, sans-serif;

    &.invoice {
        font-size: 13px;
        color: black;
        -webkit-user-select: text;
        -webkit-app-region: none;

        p {
            padding: 1px 0 !important;
            margin: 0 !important;
        }

        .mt-2 {
            margin-top: 8px !important;
        }

        .divider {
            border-top: 1px solid black;
            height: 1px !important;
        }

        .receipt-header {
            padding: 10px;
            text-align: center;
            font-size: 16px;
            font-weight: 500;
        }

        .nonfiscal-heading {
            padding: 10px;
        }

        .receipt-subheader {
            width: 100%;
            padding: 10px;
            display: flex;
            flex-wrap: wrap-reverse;
            align-items: center;

            .receipt-details {
                flex: 1;
                min-width: 300px;
            }

            .receipt-buyer {
                border-radius: 4px;
                border: 1px solid black;
                width: 250px;
                padding: 10px;
                margin: 10px 10px 10px 0;
            }
        }

        .receipt-items {
            margin-top: 10px;
            border-collapse: collapse;
            width: 100%;

            tbody {

                tr {
                    vertical-align: top;

                    &.border-bottom {
                        border-bottom: 1px solid #b0b0b0;
                    }

                    td {
                        padding: 4px;

                        &:first-child {
                            padding-left: 10px;
                        }

                        &.name {
                            min-width: 130px;
                            word-break: break-word;
                            white-space: normal;
                        }

                        &:last-child {
                            padding-right: 10px;
                        }
                    }

                    &:hover {
                        background: #f7f7f7;
                    }
                }

                .heading-row {
                    padding: 0 5px;
                    vertical-align: middle;
                    border-top: 1px solid black;
                    border-bottom: 1px solid black;
                    font-size: 13px;
                    font-weight: 600;
                    background: #f1f1f1;
                }
            }
        }

        .tax-items {
            margin-top: 25px;
            border-collapse: collapse;
            width: 100%;

            tbody {

                tr {
                    td {
                        padding: 4px;
                    }

                    &:hover {
                        background: #f7f7f7;
                    }

                    &.bordered {
                        border: 1px solid black;
                        border-width: 1px 0;
                    }
                }

                .heading-row {
                    padding: 0 5px;
                    vertical-align: middle;
                    border-top: 1px solid black;
                    border-bottom: 1px solid black;
                    font-size: 13px;
                    font-weight: 600;
                    background: #f1f1f1;
                }
            }
        }

        .receipt-footer {
            display: flex;
            flex-wrap: wrap-reverse;
            margin-bottom: 20px;
            break-inside: avoid;

            .footer-item {
                flex: 1;

                &.additional-info {
                    flex: 2;
                    padding: 10px 0 10px 10px;

                    .code {
                        display: block;
                        a {
                            text-decoration: none;
                        }
                    }

                    .signatures {
                        display: flex;
                        flex-wrap: wrap;

                        .signature {
                            margin-top: 20px;
                            text-align: center;

                            &:first-child {
                                margin-right: 20px;
                            }
                        }
                    }
                }

                &.summary {
                    padding: 0px 10px 10px 0;

                    .summary-item {
                        padding: 1px;
                        display: flex;
                        justify-content: space-between;

                        &.to-pay {
                            font-weight: 500;
                            font-size: 18px;
                            display: flex;
                            align-items: center;
                        }

                        .amount {
                            text-align: right;
                        }
                    }
                }
            }
        }
    }
}
</style>
